import PropTypes from "prop-types";
import React, { useEffect } from "react";
import AdminPageLayout from "../../../../components/FullPageLayout/AdminPageLayout";
import { Tabs, Tab, Button } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { push } from "connected-react-router";
import { Route, Redirect } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchCourses } from "../../../../redux/actions/contentCoursesActions";
import {
  courses,
  courseDetailsVersions,
  courseDetailsAssignments,
  courseDetailsExclusions,
  courseDetailsRecordings,
  courseDetailsSessions,
  courseDetails,
  buildCourseDetailsSettingsRoute,
  buildCourseDetailsVersionsRoute,
  buildCourseDetailsSessionsRoute,
  buildCourseDetailsAssignmentsRoute,
  buildCourseDetailsExclusionsRoute,
  buildCourseDetailsRecordingsRoute,
  courseDetailsSettings
} from "../../../../routes/AdminRoutes";
import {
  CourseSettings,
  CourseVersions,
  CourseAssignments,
  CourseExclusions,
  CourseSessions
} from ".";
import LoadingSpinner from "../../../../components/LoadingSpinner/LoadingSpinner";
import { contentOwners, courseTypes } from "../../../../constants";
import { pullAt } from "lodash";
import Breadcrumb from "../../../../components/Breadcrumb/Breadcrumb";
import { makeGetEntities } from "../../../../redux/selectors/selectors";
import usePermissions from "../../../../redux/selectors/usePermissions";
import CourseRecordings from "./CourseRecordings";
import { isIframed } from "../../../../utils/iframe";

const courseSelector = makeGetEntities();

const getVersionOrSessionTab = (courseId, courseType) => {
  return courseType === courseTypes.ilt
    ? {
        route: buildCourseDetailsSessionsRoute(courseId),
        title: "manageCourses.courseSessionsTitle"
      }
    : {
        route: buildCourseDetailsVersionsRoute(courseId),
        title: "manageCourses.courseVersionsTitle"
      };
};

const getCourseDetailsTabs = (
  courseId,
  сustomizableContent,
  courseType,
  isInstructor
) => {
  let routes = [getVersionOrSessionTab(courseId, courseType)];

  if (!isInstructor) {
    routes = [
      ...(isIframed ? routes : []),
      {
        route: buildCourseDetailsSettingsRoute(courseId),
        component: CourseSettings,
        title: "manageCourses.courseSettingsTitle"
      },
      ...(isIframed
        ? []
        : [
            ...routes,
            {
              route: buildCourseDetailsAssignmentsRoute(courseId),
              component: CourseAssignments,
              title: "manageCourses.courseAssignmentsTitle"
            },
            {
              route: buildCourseDetailsExclusionsRoute(courseId),
              component: courseDetailsExclusions,
              title: "manageCourses.courseExclusionsTitle"
            }
          ]),
      ...(courseType === courseTypes.ilt
        ? [
            {
              route: buildCourseDetailsRecordingsRoute(courseId),
              component: null,
              title: "manageCourses.courseRecordingsTitle"
            }
          ]
        : [])
    ];
  }

  if (!сustomizableContent) {
    pullAt(routes, 1);
  }

  return routes;
};

const CourseDetails = ({
  courseId,
  courseData,
  onGoBackClick,
  onTabChange,
  pathname
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [tabs, setTabs] = React.useState([]);
  const [currentTab, setCurrentTab] = React.useState(0);
  const { canManageInstructors, isInstructor } = usePermissions();

  useEffect(() => {
    if (courseData) {
      const { type, contentOwner } = courseData;
      const isCustomizable =
        contentOwner === contentOwners.custom ||
        contentOwner === contentOwners.master;
      setTabs(
        getCourseDetailsTabs(courseId, isCustomizable, type, isInstructor)
      );
    }
  }, [courseData]);

  useEffect(() => {
    const currentPathIndex = tabs.findIndex(tab => tab.route === pathname);
    if (currentPathIndex !== -1) {
      setCurrentTab(currentPathIndex);
    }
  }, [pathname, tabs]);

  const handleTabChange = newTab => {
    setCurrentTab(newTab);
    onTabChange(tabs[newTab]);
  };

  return (
    <AdminPageLayout
      title={
        <Breadcrumb routes={["Courses", courseData ? courseData.name : ""]} />
      }
      headerActions={
        courseData &&
        courseData.type === courseTypes.ilt &&
        canManageInstructors && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => dispatch(push(`${pathname}/manage`))}
            data-qa="add-course-btn"
          >
            {t("manageCourses.ManangeInstrAndLocButton")}
          </Button>
        )
      }
      onGoBackClick={onGoBackClick}
    >
      {courseData ? (
        <>
          <Tabs
            value={currentTab}
            onChange={(e, newTab) => handleTabChange(newTab)}
            indicatorColor="primary"
            textColor="primary"
            variant="scrollable"
            scrollButtons={false}
          >
            {tabs.map((tab, index) => (
              <Tab
                key={index}
                label={t(tab.title)}
                data-qa="course-details-tab"
              />
            ))}
          </Tabs>
          {!isInstructor && (
            <>
              <Route
                exact
                path={courseDetails}
                render={() => (
                  <Redirect to={buildCourseDetailsSettingsRoute(courseId)} />
                )}
              />
              <Route
                exact
                path={courseDetailsSettings}
                render={props => (
                  <CourseSettings course={courseData} {...props} />
                )}
              />
              <Route
                exact
                path={courseDetailsAssignments}
                component={CourseAssignments}
              />
              <Route
                exact
                path={courseDetailsExclusions}
                render={({
                  match: {
                    params: { courseId }
                  }
                }) => <CourseExclusions courseId={courseId} />}
              />
              <Route
                exact
                path={courseDetailsRecordings}
                render={props => (
                  <CourseRecordings courseId={courseId} {...props} />
                )}
              />
              {(courseData.contentOwner === contentOwners.custom ||
                courseData.contentOwner === contentOwners.master) && (
                <Route
                  exact
                  path={courseDetailsVersions}
                  component={CourseVersions}
                />
              )}
            </>
          )}
          <Route
            exact
            path={courseDetailsSessions}
            component={CourseSessions}
          />
        </>
      ) : (
        <LoadingSpinner />
      )}
    </AdminPageLayout>
  );
};

const CourseDetailsContainer = ({
  match: {
    params: { courseId }
  },
  location: { pathname }
}) => {
  const dispatch = useDispatch();
  const { courseData } = useSelector(({ contentCoursesState }) => ({
    courseData: courseSelector(contentCoursesState, {
      type: "trainingObject",
      id: courseId
    }),
    loading: contentCoursesState.loading
  }));
  const { isInstructor } = usePermissions();

  if (isInstructor && pathname !== buildCourseDetailsSessionsRoute(courseId)) {
    dispatch(push(buildCourseDetailsSessionsRoute(courseId)));
  }

  useEffect(() => {
    if (courseId) {
      dispatch(fetchCourses(courseId));
    }
  }, [courseId]);

  function handleTabChange(newTab) {
    dispatch(push(newTab.route));
  }

  const handleGoBack = () => {
    dispatch(push(courses));
  };

  return (
    <CourseDetails
      courseId={courseId}
      onGoBackClick={handleGoBack}
      courseData={courseData}
      onTabChange={handleTabChange}
      pathname={pathname}
    />
  );
};

CourseDetailsContainer.propTypes = {
  match: PropTypes.shape({
    params: PropTypes.shape({
      /**
       * The course ID for the course
       */
      courseId: PropTypes.string
    }),
    path: PropTypes.string,
    url: PropTypes.string
  }),
  location: PropTypes.shape({
    pathname: PropTypes.string
  })
};

export default CourseDetailsContainer;
